<script setup lang="ts">
import { ref, useTemplateRef, watch } from 'vue';

interface SearchInputProps {
	placeholder?: string;
	disabled?: boolean;
	loading?: boolean;
}

defineProps<SearchInputProps>();

const searchValue = defineModel<string>({ default: '' });

const isExpanded = ref(!!searchValue.value);
const isButtonShown = ref(!searchValue.value);
const inputRef = useTemplateRef('input');

watch(searchValue, (newValue) => {
	if (newValue && !isExpanded.value) {
		expandSearch();
	}
}, { immediate: true });

function expandSearch() {
	isExpanded.value = true;
	isButtonShown.value = false;
}

function collapseSearch() {
	if (!searchValue.value) {
		isExpanded.value = false;
	}
	else {
		searchValue.value = '';
	}
}

function clearSearch() {
	searchValue.value = '';
	inputRef.value?.inputRef?.focus();
}

function handleBlur() {
	if (!searchValue.value) {
		collapseSearch();
	}
}
</script>

<template>
	<div class="flex h-6 items-center">
		<Transition
			enter-active-class="transition-[width] duration-100 ease-out [&_input]:pe-0 **:data-[slot=trailing]:transition-opacity **:data-[slot=trailing]:duration-100 **:data-[slot=leadingIcon]:transition-colors **:data-[slot=leadingIcon]:duration-100 [&_input]:placeholder:transition-opacity [&_input]:placeholder:duration-100"
			enter-from-class="w-8! **:data-[slot=trailing]:opacity-0 **:data-[slot=leadingIcon]:text-default [&_input]:placeholder:opacity-0"
			leave-active-class="transition-[width] duration-100 ease-out [&_input]:pe-0 **:data-[slot=trailing]:transition-opacity **:data-[slot=trailing]:duration-100 **:data-[slot=leadingIcon]:transition-colors **:data-[slot=leadingIcon]:duration-100 [&_input]:placeholder:transition-opacity [&_input]:placeholder:duration-100"
			leave-to-class="w-8! **:data-[slot=trailing]:opacity-0 **:data-[slot=leadingIcon]:text-default [&_input]:placeholder:opacity-0"
			@after-leave="isButtonShown = true"
		>
			<div
				v-if="isExpanded"
				class="w-56 overflow-hidden"
			>
				<DInput
					ref="input"
					v-model="searchValue"
					class="w-full"
					size="sm"
					autofocus
					data-testid="search-input"
					:placeholder="placeholder ?? $t('common.search')"
					:disabled
					:loading
					leading-icon="lucide:search"
					:ui="{ base: 'py-1.5', trailing: 'pe-0.5' }"
					@keydown.escape="collapseSearch"
					@blur="handleBlur"
				>
					<template #trailing>
						<DButton
							data-testid="search-clear-button"
							variant="ghost"
							color="neutral"
							size="xs"
							icon="lucide:x"
							class="rounded-sm opacity-100"
							@click="searchValue ? clearSearch() : collapseSearch()"
						/>
					</template>
				</DInput>
			</div>
		</Transition>
		<DTooltip v-if="isButtonShown" :text="$t('common.search')">
			<DButton
				data-testid="search-button"
				variant="outline"
				size="sm"
				color="neutral"
				icon="lucide:search"
				:aria-label="$t('common.search')"
				@click="expandSearch"
			/>
		</DTooltip>
	</div>
</template>
